import { useParams } from "react-router-dom";
import { PageHeader } from "@/components/data-display/page-header";
import { LoadingState, NotAvailableState } from "@/components/ui/system-state";
import { EmptyState } from "@/components/ui/feedback";
import { SupportDetails } from "@/components/ui/copyable-reference";
import { HotWalletTabs } from "@/features/hot-wallets/hot-wallet-tabs";
import { useHotWalletReconciliation } from "@/hooks/use-hot-wallet-reconciliation";
import { useHotWalletsLocale } from "@/i18n/use-hot-wallets-locale";

/**
 * HW-S4: reconciliation (SPEC-HW-8, WORKFLOW.md §3). Named
 * `/hot-wallets/:walletId/reconciliation` — distinct from the existing
 * generic `/reconciliation` page, which is the tenant-ledger-wide surface
 * for SPEC-FR (ReconFlow) and stays untouched by this lane.
 *
 * SPEC-HW-8 rule 5 ("reconciliation truth"): a provisional statement must be
 * labelled as such, with a reason, never presented as final. This page
 * carries that label through from `statementStatus`; it renders neither
 * value until a real statement exists.
 */
export default function HotWalletReconciliationPage() {
  const { walletId = "" } = useParams<{ walletId: string }>();
  const { ui, dir } = useHotWalletsLocale();
  const { summary, loading, error, refresh } = useHotWalletReconciliation(walletId);

  const statementLabel =
    summary?.statementStatus === "final"
      ? ui["reconciliation.finalLabel"]
      : summary?.statementStatus === "provisional"
        ? ui["reconciliation.provisionalLabel"]
        : null;

  return (
    <div dir={dir}>
      <PageHeader title={ui["reconciliation.title"]} subtitle={ui["reconciliation.subtitle"]} />
      <HotWalletTabs walletId={walletId} active="reconciliation" labels={ui} />

      {loading && <LoadingState rows={3} />}

      {!loading && (error || !summary) && (
        <div className="flex flex-col items-center gap-2">
          <NotAvailableState
            title={ui["reconciliation.unavailableTitle"]}
            description={ui["reconciliation.unavailableDescription"]}
            reason={error?.reasonCode}
          />
          {error && (
            <SupportDetails reference={error.supportReference} capability={error.capability} className="justify-center" />
          )}
          <button
            type="button"
            onClick={refresh}
            className="inline-flex min-h-[36px] items-center text-sm font-medium text-primary underline"
          >
            {ui["common.retry"]}
          </button>
        </div>
      )}

      {!loading && !error && summary && !summary.businessDate && (
        <EmptyState card title={ui["reconciliation.emptyTitle"]} description={ui["reconciliation.emptyDescription"]} />
      )}

      {!loading && !error && summary && summary.businessDate && (
        <div className="rounded-lg border border-line bg-surface px-5 py-5">
          {/* The date and its statement badge wrap on a phone instead of
              fighting for one line: the badge is a fixed-size pill, so a long
              business date would otherwise push it past the viewport. */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="min-w-0 break-words text-sm font-semibold text-ink">{summary.businessDate}</span>
            {statementLabel && (
              <span
                className={
                  summary.statementStatus === "final"
                    ? "shrink-0 rounded-full bg-success-soft px-2.5 py-1 text-xs font-semibold text-success"
                    : "shrink-0 rounded-full bg-warning-soft px-2.5 py-1 text-xs font-semibold text-warning"
                }
              >
                {statementLabel}
              </span>
            )}
          </div>
          <div className="mt-3 text-sm text-muted" data-testid="hot-wallet-recon-readout">
            {/* `openExceptionCount` is optional on the wire: an absent count is
                unknown, not zero, and a rendered "0" would claim an
                exception-free ledger nothing in the response asserts. */}
            {summary.identityHolds ? "✓" : "—"} · {summary.openExceptionCount ?? "—"}
          </div>
        </div>
      )}
    </div>
  );
}
